import React, { ReactNode } from 'react';
import { Box, Button, Chip, Grid, Tooltip, Typography, IconButton } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import makeStyles from '@mui/styles/makeStyles';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import { InsuranceStatusBox } from '../common/InsuranceStatusBox';
import { useModal } from 'hooks/useModal';
import { ModalsKeys } from 'global';
import { insuranceActions, insuranceSelector } from '../../model';
import { coverageStatusConfig } from 'modules/vendorOpportunity/insurance/constants';
import { CoverageStatusLabels } from 'modules/vendorOpportunity/insurance/types';
import { useDispatch, useSelector } from 'react-redux';
import { ModalsContainer } from 'components/shared/Modals';
import { InsuranceAddEditBrokerDialog } from '../dialogs';
import { InsuranceBrokerDetails } from '../insuranceAccordion/agentAndPolicyDetails/InsuranceBrokerDetails';
import FileUploadModal from 'pages/VendorOpportunity/FileUploadModal';
import { documentsByKey } from 'pages/Prequal/constants';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Q_DOCS, UPDATE_DOCUMENTS_LENDER_ACCESS } from '../../api';
import { useNotifications } from 'modules/notification';
import { UploadedFileRow } from '../insuranceAccordion/certificateOfInsurance/UploadedFileRow';
import { getInsuranceTaskInput } from '../../lib';
import { UPSERT_TASK } from 'pages/VendorOpportunity/CustomerSummaryCard_V2/queries';

const config = require('../../../../../config');

interface Props {
  coiStatus: 'Not Received' | 'Received' | 'Pending' | 'Sent To Lender';
  onSendInsuranceQuoteToApplicant: () => void;
  onUploadCoiManually?: () => void;
}

export const CoiAcquisitionSection = ({ coiStatus, onSendInsuranceQuoteToApplicant, onUploadCoiManually }: Props) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const { showNotification } = useNotifications();

  const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
    [ModalsKeys.InsuranceAddEditBrokerDialog]: <InsuranceAddEditBrokerDialog />,
  };

  const { handleOpen: handleOpenInsuranceBrokerDialog } = useModal(ModalsKeys.InsuranceAddEditBrokerDialog);

  const insuranceBroker = useSelector(insuranceSelector.insuranceBroker);
  const proposals = useSelector(insuranceSelector.proposals);
  const vo = useSelector(insuranceSelector.vendorOpportunity);
  // @ts-ignore
  const account = useSelector(state => state.account);
  const creditApp = useSelector((state: any) => state.creditApp);
  const userProfile = useSelector((state: any) => state.userProfile);
  const selectedSubmission = useSelector(insuranceSelector.selectedSubmission);
  const insuranceCertificate = useSelector(insuranceSelector.insuranceCertificate);
  const selectedFinanceProgram = useSelector(insuranceSelector.selectedFinanceProgram);
  const insuranceVerificationTask = useSelector(insuranceSelector.insuranceVerificationTask);

  const insuranceQuote = useSelector(insuranceSelector.insuranceQuote);

  const hasBrokerDetails =
    !!insuranceBroker &&
    (!!insuranceBroker.contactName ||
      !!insuranceBroker.companyName ||
      !!insuranceBroker.contactEmail ||
      !!insuranceBroker.contactPhone ||
      !!insuranceBroker.renewalDate ||
      !!insuranceBroker.physicalDamageDeduction ||
      !!insuranceBroker.liabilityCoverage ||
      !!insuranceBroker.productType);

  const [updateLenderDocumentsAccess] = useMutation(UPDATE_DOCUMENTS_LENDER_ACCESS);

  const [getDocument] = useLazyQuery(Q_DOCS, {
    onCompleted(docResponse) {
      try {
        dispatch(insuranceActions.setInsuranceCertificate(docResponse.documents[0]));
        dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredVerified]));
        showNotification('Insurance Certificate Uploaded Successfully!');
      } catch (e) {
        showNotification('Error Retrieving COI, please reload this page.', { type: 'error' });
      }
    },
  });

  const sendToLender = async () => {
    try {
      if (!selectedSubmission || !insuranceCertificate) {
        showNotification('Please select a submission and upload a certificate before sending to lender.', {
          type: 'error',
        });
        return;
      }

      await updateLenderDocumentsAccess({
        variables: {
          batchUpdateLenderDocumentAccessInput: {
            creditSubmissionId: selectedSubmission?.creditSubId,
            documents: [
              {
                documentId: insuranceCertificate?.documentId,
                lenderAccess: true,
                dealerAccess: true,
              },
            ],
          },
        },
      });

      dispatch(
        insuranceActions.setInsuranceCertificate({
          ...insuranceCertificate,
          creditSubmissionId: selectedSubmission?.creditSubId,
        })
      );
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  const handleOnFileUploaded = async ({ document, success }: { document: string; success: boolean }) => {
    if (document && success) {
      await getDocument({ variables: { docId: document } });
    } else {
      showNotification('Error saving COI. Please try again.', { type: 'error' });
    }
  };

  const handleDeleteFile = () => {
    dispatch(insuranceActions.setInsuranceCertificate(null));
    dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredUnverified]));
  };

  const [upsertTask] = useMutation(UPSERT_TASK);

  const handleVerifyCoverage = async () => {
    try {
      const taskInput = getInsuranceTaskInput(
        vo!.vendorOpportunityId!,
        creditApp.id,
        selectedFinanceProgram?.financeProgramId!,
        userProfile,
        account,
        selectedSubmission!,
        insuranceBroker!
      );

      const upsertResult = await upsertTask({ variables: { input: taskInput } });

      dispatch(
        insuranceActions.setInsuranceVerificationTask({
          ...taskInput,
          taskId: upsertResult?.data?.upsertTask?.id,
        })
      );

      showNotification('Task successfully created!');
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleGoToTaskPage = () => {
    const link = `https://app.${config.environmentName === 'dev' ? 'dev.' : ''}trnsact.com/task-page?taskId=${
      insuranceVerificationTask?.taskId
    }&lenderProfileDynamicsId=${selectedSubmission?.lenderProfileId}&vendorGUID=${account.dynamicsAccountId}`;
    window.open(link, '_blank');
  };

  const normalizeDate = (value?: string | null) => {
    if (!value) return null;
    const normalized = value.replace(' ', 'T').replace('+00', 'Z');
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatProposalDate = (value?: string | null) => {
    const d = normalizeDate(value);
    if (!d) return '';
    return d.toLocaleString(undefined, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const sortedProposals = (Array.isArray(proposals) ? [...proposals] : []).sort((a: any, b: any) => {
    const aTime = normalizeDate(a?.createdDateTime)?.getTime() ?? 0;
    const bTime = normalizeDate(b?.createdDateTime)?.getTime() ?? 0;
    return bTime - aTime;
  });

  const hasAnyProposals = sortedProposals.length > 0;

  const renderCoiStatus = () => {
    const normalized = (coiStatus || '').toString().trim().toLowerCase();
    const isNotReceived = normalized === 'not received';
    const isReceived = normalized === 'received';
    const isSentToLender = normalized === 'sent to lender';

    if (isNotReceived) {
      return (
        <Box className={classes.coiStatusInline}>
          <WarningAmberOutlinedIcon className={classes.coiWarnIcon} fontSize="small" />
          <Typography className={classes.coiWarnText}>Not Received</Typography>
        </Box>
      );
    }

    if (isReceived) {
      return (
        <Box className={classes.coiStatusInline}>
          <ArrowDownwardOutlinedIcon className={classes.coiInfoIcon} fontSize="small" />
          <Typography className={classes.coiInfoText}>Received</Typography>
        </Box>
      );
    }

    if (isSentToLender) {
      return (
        <Box className={classes.coiStatusInline}>
          <CheckCircleOutlineIcon className={classes.coiSuccessIcon} fontSize="small" />
          <Typography className={classes.coiSuccessText}>Sent To Lender</Typography>
        </Box>
      );
    }

    return <Typography className={classes.coiDefaultText}>{coiStatus}</Typography>;
  };

  return (
    <Box>
      <Box className={classes.headerRow}>
        <Typography className={classes.sectionTitle}>Certificate of Insurance (COI) Acquisition</Typography>
        <InsuranceStatusBox label="Certificate of Insurance Status" statusText={renderCoiStatus()} />
      </Box>

      <Typography className={classes.sectionSubtitle}>
        Send an insurance quote or obtain a Certificate of Insurance through the agent.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box className={classes.card}>
            <Box className={`${classes.cardHeader} ${classes.cardHeaderBlue}`}>
              <Box className={classes.headerLeft}>
                <Box className={`${classes.headerIcon} ${classes.headerIconBlue}`}>
                  <PlayArrowOutlinedIcon fontSize="small" />
                </Box>

                <Typography className={classes.headerTitleBlue}>Dealer Quoted Insurance</Typography>

                <Chip
                  size="small"
                  icon={<SpeedOutlinedIcon />}
                  label="Faster"
                  className={classes.fasterChip}
                  variant="outlined"
                />
              </Box>

              <ChevronRightIcon className={classes.headerChevron} />
            </Box>

            <Box className={classes.cardBodyPad}>
              <Typography className={classes.leftBodyTop}>
                To continue, add agent details and submit the Certificate of Insurance.
              </Typography>

              {hasAnyProposals ? (
                <Box className={classes.proposalsList}>
                  {sortedProposals.map((p: any) => {
                    const proposalId = p?.proposalId ?? p?.id;
                    const vendorGuid = p?.dynamicsAccountId;

                    const portalUrl = `https://portal${config.environmentName === 'dev' ? '.dev' : ''}.trnsact.com/proposalDetail/${proposalId}?from=/proposals`;
                    const externalUrl = `https://app${config.environmentName === 'dev' ? '.dev' : ''}.trnsact.com/ps/?vendorGUID=${vendorGuid}&proposalId=${proposalId}&viewOnly=true`;

                    // Assume just one menu option (first paymentCriteria.menu.menuOptions[0])
                    const menuOption = p?.lenderPaymentOptions?.[0]?.paymentCriteria?.menu?.menuOptions?.[0];

                    // Products + payment amount
                    const products: any[] = Array.isArray(menuOption?.products) ? menuOption.products : [];
                    const paymentAmount =
                      menuOption?.payment?.paymentAmountPerPeriod ??
                      menuOption?.payment?.paymentAmountPerPeriodWithoutDealerMarkup ??
                      p?.lenderPaymentOptions?.[0]?.paymentCriteria?.paymentAmountPerPeriod ??
                      null;

                    const money = (val: any) => {
                      const n = Number(val);
                      if (!Number.isFinite(n)) return '—';
                      return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
                    };

                    return (
                      <Box key={proposalId} className={classes.proposalRow}>
                        <Box className={classes.proposalRowTop}>
                          <Box className={classes.proposalStatusLeft}>
                            <CheckCircleOutlineIcon className={classes.leftStatusOkIcon} />
                            <Typography className={classes.leftStatusOkText}>{p?.status || '—'}</Typography>
                          </Box>

                          <Box className={classes.proposalRightSide}>
                            <Typography className={classes.leftStatusTime}>
                              {formatProposalDate(p?.createdDateTime)}
                            </Typography>

                            <Box className={classes.proposalActions}>
                              <Tooltip title="View proposal details" arrow placement="top">
                                <IconButton
                                  size="small"
                                  className={classes.proposalIconBtn}
                                  onClick={() => openInNewTab(portalUrl)}
                                >
                                  <VisibilityOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Open external view" arrow placement="top">
                                <IconButton
                                  size="small"
                                  className={classes.proposalIconBtn}
                                  onClick={() => openInNewTab(externalUrl)}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>

                        <Box className={classes.proposalMetaRow}>
                          <Box className={classes.proposalProductsWrap}>
                            {products.length > 0 ? (
                              products.map((prod: any) => (
                                <Box
                                  key={prod?.proposalProductId ?? prod?.title}
                                  className={classes.productPill}
                                  title={prod?.title || ''}
                                >
                                  <Typography className={classes.productPillText}>
                                    {prod?.title || 'Product'}
                                  </Typography>
                                  <Typography className={classes.productPillSubText}>
                                    {money(prod?.retailCost ?? prod?.cost)}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography className={classes.proposalProductsEmpty}>No products</Typography>
                            )}
                          </Box>

                          {/* <Box className={classes.proposalAmountBox}>
                          <Typography className={classes.proposalAmountLabel}>Payment</Typography>
                          <Typography className={classes.proposalAmountValue}>{money(paymentAmount)}</Typography>
                        </Box> */}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box className={classes.noProposalsBox}>
                  <Typography className={classes.noProposalsTitle}>No insurance quotes yet</Typography>
                  <Typography className={classes.noProposalsText}>
                    There are no insurance quotes on this deal. Use the button below to send one!
                  </Typography>
                </Box>
              )}

              <Typography className={classes.leftStatusSubText}>
                {hasAnyProposals ? 'Certificate of Insurance Required' : ' '}
              </Typography>

              <Tooltip
                title={
                  hasBrokerDetails
                    ? ''
                    : 'You can send multiple quotes to the applicant. Do this if details change or the applicant wants different coverage.'
                }
                placement="top"
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.sendQuoteBtn}
                    onClick={onSendInsuranceQuoteToApplicant}
                  >
                    SEND{hasAnyProposals ? ' ANOTHER' : ''} QUOTE TO APPLICANT
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className={classes.card}>
            <Box className={`${classes.cardHeader} ${classes.cardHeaderGray}`}>
              <Box className={classes.headerLeft}>
                <Box className={`${classes.headerIcon} ${classes.headerIconGray}`}>
                  <PersonOutlineIcon fontSize="small" />
                </Box>

                <Typography className={classes.headerTitleGray}>Agent-provided COI</Typography>
              </Box>

              <Button
                variant="outlined"
                className={classes.editDetailsBtn}
                onClick={() => (handleOpenInsuranceBrokerDialog ? handleOpenInsuranceBrokerDialog() : undefined)}
              >
                {hasBrokerDetails ? 'EDIT DETAILS' : 'ADD AGENT DETAILS'}
              </Button>
            </Box>

            <Box className={classes.cardBodyPad}>
              <Typography className={classes.rightBodyTop}>Request COI from the insurance agent.</Typography>

              {hasBrokerDetails && <InsuranceBrokerDetails />}

              <Box className={classes.actionRow}>
                {insuranceVerificationTask && (
                  <Box display="flex" justifyContent="space-between" width="100%" flexWrap="wrap" gap={1}>
                    <Box display="flex" alignItems="center">
                      <CheckCircleOutlineIcon color="primary" />
                      <Typography mb={2} ml={0.5} component="p" variant="body1" style={{ paddingTop: 16 }}>
                        You have sent the request for a COI to the Insurance Agent
                      </Typography>
                    </Box>

                    <Box>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={handleGoToTaskPage}
                        sx={{ ml: 1 }}
                      >
                        Open task page
                      </Button>
                    </Box>
                  </Box>
                )}
                <>
                  <Tooltip
                    title='Click "ADD AGENT DETAILS" above to provide agent information before requesting the COI. You must also select a submission and finance program.'
                    placement="bottom"
                    arrow
                  >
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        variant="outlined"
                        className={classes.requestBtn}
                        disabled={
                          !hasBrokerDetails || ((!selectedSubmission || !selectedFinanceProgram) && !insuranceQuote)
                        }
                        onClick={() => {
                          handleVerifyCoverage();
                        }}
                      >
                        REQUEST COI FROM AGENT
                      </Button>
                    </span>
                  </Tooltip>

                  <FileUploadModal
                    vo={vo}
                    vp={account.vendorProfile}
                    equipmentUpload={false}
                    buttonDescription={() => (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <CloudUploadOutlinedIcon fontSize="small" style={{ color: '#2563eb' }} />
                        <span
                          style={{ color: '#2563eb', fontWeight: 700, fontSize: 11, marginBottom: 4, paddingTop: 2 }}
                        >
                          UPLOAD COI MANUALLY
                        </span>
                      </span>
                    )}
                    creditSubmissionId={selectedSubmission?.creditSubId}
                    predefinedFileType={documentsByKey.insurance_certificate.value}
                    relatedTo="submission"
                    onFileUploaded={handleOnFileUploaded}
                  />

                  <UploadedFileRow onRemove={handleDeleteFile} onSendToLender={sendToLender} />
                </>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </Box>
  );
};

const useStyles = makeStyles({
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: '#111827',
  },
  sectionSubtitle: {
    marginTop: 6,
    marginBottom: 12,
    fontSize: 12,
    color: '#4b5563',
  },

  coiStatusInline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    paddingTop: 4,
  },
  coiWarnIcon: {
    color: '#f59e0b',
  },
  coiWarnText: {
    color: '#f59e0b',
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  coiInfoIcon: {
    color: '#2563eb',
  },
  coiInfoText: {
    color: '#2563eb',
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  coiSuccessIcon: {
    color: '#22c55e',
  },
  coiSuccessText: {
    color: '#22c55e',
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  coiDefaultText: {
    color: '#111827',
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },

  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    height: '100%',
  },

  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    borderBottom: '1px solid #e5e7eb',
    gap: 10,
  },
  cardHeaderBlue: {
    backgroundColor: '#eaf4ff',
  },
  cardHeaderGray: {
    backgroundColor: '#eef2f5',
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },

  headerIcon: {
    width: 28,
    height: 28,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIconBlue: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
  },
  headerIconGray: {
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
  },

  headerTitleBlue: {
    fontSize: 12,
    fontWeight: 700,
    color: '#1d4ed8',
    whiteSpace: 'nowrap',
  },
  headerTitleGray: {
    fontSize: 12,
    fontWeight: 700,
    color: '#111827',
    whiteSpace: 'nowrap',
  },

  headerChevron: {
    color: '#2563eb',
    fontSize: 18,
    flexShrink: 0,
  },

  fasterChip: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    fontSize: 11,
    height: 22,
    '& .MuiChip-icon': {
      color: '#1d4ed8',
    },
  },

  editDetailsBtn: {
    borderColor: '#93c5fd',
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    minHeight: 26,
    textTransform: 'none',
    borderRadius: 4,
    whiteSpace: 'nowrap',
  },

  cardBodyPad: {
    padding: 12,
  },

  leftBodyTop: {
    fontSize: 12,
    color: '#111827',
    marginBottom: 10,
  },

  proposalsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 10,
  },

  proposalRow: {
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '8px 10px',
  },

  proposalRowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },

  proposalStatusLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },

  proposalRightSide: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  proposalActions: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },

  proposalIconBtn: {
    padding: 4,
    color: '#2563eb',
  },

  proposalBusinessName: {
    fontSize: 11,
    color: '#374151',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    flex: 1,
  },

  proposalMetaRow: {
    marginTop: 8,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },

  proposalProductsWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    minWidth: 0,
    flex: 1,
  },

  proposalProductsEmpty: {
    fontSize: 11,
    color: '#6b7280',
  },

  productPill: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 2,
    padding: '6px 8px',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    maxWidth: 240,
  },

  productPillText: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
  },

  productPillSubText: {
    fontSize: 11,
    color: '#6b7280',
    lineHeight: 1.2,
  },

  proposalAmountBox: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '6px 8px',
    borderRadius: 8,
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    minWidth: 110,
  },

  proposalAmountLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#1d4ed8',
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },

  proposalAmountValue: {
    fontSize: 12,
    fontWeight: 800,
    color: '#1d4ed8',
    lineHeight: 1.2,
  },

  proposalMetaRight: {
    fontSize: 11,
    color: '#6b7280',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  noProposalsBox: {
    border: '1px dashed #cbd5e1',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  noProposalsTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 4,
  },
  noProposalsText: {
    fontSize: 12,
    color: '#4b5563',
    lineHeight: 1.4,
  },

  leftStatusOkIcon: {
    fontSize: 18,
    color: '#22c55e',
  },
  leftStatusOkText: {
    fontSize: 12,
    fontWeight: 600,
    color: '#22c55e',
  },
  leftStatusTime: {
    fontSize: 11,
    color: '#6b7280',
  },
  leftStatusSubText: {
    marginTop: 4,
    fontSize: 11,
    color: '#6b7280',
    minHeight: 14,
  },

  sendQuoteBtn: {
    marginTop: 8,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: 4,
    padding: '6px 12px',
  },

  rightBodyTop: {
    fontSize: 12,
    color: '#111827',
    marginBottom: 10,
  },

  actionRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    paddingTop: 10,
  },
  requestBtn: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: 4,
    padding: '6px 12px',
    borderColor: '#93c5fd',
    color: '#2563eb',
    backgroundColor: '#ffffff',
  },

  '@media (max-width: 900px)': {
    primaryCtaHiddenOnDesktop: {
      display: 'inline-flex',
    },
  },
});
